import { useMemo, useState } from "react";
import type { GroomingBatch, GroomingBatchItemView } from "@ttc-spectree/toolbox-contract";
import { AdoContent } from "@/components/ado/ado-content";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { postErrorText } from "@/components/grooming/brief-review";
import { useBriefCommentPreview, usePostHeldBriefs, type PostAllResult } from "@/hooks/queries/use-grooming";
import { useMe } from "@/hooks/queries/use-me";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * Post all (epic PI-SPEC-SW-E279, task PI-SPEC-SW-1392-3; Claude Design
 * "All Screens" frame 2f): the one action that reaches the team, so it shows
 * exactly what lands in ADO before it goes.
 *
 * The preview is the server's exact comment HTML (`GET .../comment-preview`)
 * rendered only through `AdoContent`, which runs `sanitizeAdoHtml` (the
 * sanitiser every tracker HTML in the web app goes through) and styles its
 * headings, lists and links as ADO's comment view does (UX-PM-31,
 * PI-SPEC-SW-1471). Posts run one by one; a failure is reported on
 * its row and its brief stays held for another try.
 *
 * Restyled to the v2 desktop design in epic PI-SPEC-SW-E280 (task
 * PI-SPEC-SW-1405-1): a status dot per row (green tick once posted), the
 * suggestion as a dashed AI chip, the preview under a plain heading and a
 * footer that says only Project Managers and Admins post. Same rows, preview and posting.
 */

/** Items whose brief is held for review: briefed, not reviewed, held or failed to post. */
export function heldBriefItems(items: readonly GroomingBatchItemView[]): GroomingBatchItemView[] {
  return items.filter(
    (item) =>
      item.status === "briefed" &&
      item.reviewState === "pending" &&
      item.brief !== null &&
      (item.brief.commentStatus === "held" || item.brief.commentStatus === "failed")
  );
}

function resultText(result: PostAllResult): string {
  if (result.ok) return "Posted";
  if (result.adoMessage !== undefined) return `ADO did not accept the comment: ${result.adoMessage}. Still held.`;
  if (result.error === null) return "ADO did not accept the comment. Still held.";
  return `${postErrorText(result.error)} Still held.`;
}

/** The row's state dot (design `.st`): a green tick once posted, a red mark when ADO refused it. */
function PostStateDot({ result }: { result: PostAllResult | undefined }) {
  return (
    <span
      className={cn(
        "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
        result?.ok === true && "bg-green-500 text-[#052e16]",
        result?.ok === false && "bg-primary/[0.18] text-red-300"
      )}
      aria-hidden="true"
    >
      {result?.ok === true ? "✓" : result?.ok === false ? "!" : ""}
    </span>
  );
}

/** The exact comment for one item, as ADO will show it. */
function CommentPreview({ project, item, posterName }: { project: string; item: GroomingBatchItemView; posterName: string }) {
  const preview = useBriefCommentPreview({ tracker: "ado", container: project, id: item.itemId }, item.brief?.id ?? null);
  const initials = posterName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  const previewFailed = (
    <p className="text-[#A4262C]" role="alert">
      The preview could not be loaded. The comment is built from the stored brief when it posts.
    </p>
  );

  return (
    <section className="flex flex-col gap-2 px-5 py-3.5" data-testid="comment-preview">
      <h3 className="font-display text-[13px] font-semibold leading-snug text-[#E4E4E7]">
        How it looks in ADO · {item.itemId}
      </h3>
      <div className="flex max-h-[260px] flex-col gap-1.5 overflow-y-auto rounded-lg bg-white px-4 py-3.5 font-['Segoe_UI',system-ui,sans-serif] text-[13px] leading-normal text-[#201F1E]">
        <div className="flex items-center gap-2 text-xs text-[#605E5C]">
          <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#8764B8] text-[10px] font-semibold text-white" aria-hidden="true">
            {initials}
          </span>
          <b className="text-[#201F1E]">{posterName}</b> commented just now
        </div>
        {preview.isLoading ? (
          <p className="text-[#605E5C]" role="status">
            Loading the comment…
          </p>
        ) : preview.isError || !preview.data ? (
          previewFailed
        ) : (
          // AdoContent sanitises with sanitizeAdoHtml and styles headings, lists and links as ADO's comment view does.
          <AdoContent html={preview.data.html} tone="light" testId="comment-preview-html" empty={previewFailed} />
        )}
        {preview.data?.stale && (
          <p className="text-[13px] text-[#8A6100]">The item changed since this brief. Post all leaves it held; review it on its own.</p>
        )}
      </div>
    </section>
  );
}

export interface PostAllDialogProps {
  project: string;
  batch: GroomingBatch;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PostAllDialog({ project, batch, open, onOpenChange }: PostAllDialogProps) {
  const { data: me } = useMe();
  const held = useMemo(() => heldBriefItems(batch.items), [batch.items]);
  const post = usePostHeldBriefs(project, batch.id);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const [focusId, setFocusId] = useState<string | null>(null);
  const [results, setResults] = useState<Map<string, PostAllResult>>(() => new Map());
  // The rows as they were when the dialog opened: posted ones leave `held` once the batch is re-read.
  const [rows, setRows] = useState<GroomingBatchItemView[]>([]);
  const [finished, setFinished] = useState(false);
  const [openedFor, setOpenedFor] = useState(false);

  // Start fresh each time the dialog opens (state adjusted during render, not in an effect).
  if (open && !openedFor) {
    setOpenedFor(true);
    setRows(held);
    setChecked(new Set(held.map((item) => item.itemId)));
    setFocusId(held[0]?.itemId ?? null);
    setResults(new Map());
    setFinished(false);
  } else if (!open && openedFor) {
    setOpenedFor(false);
  }

  const posterName = me?.name ?? "you";
  const focus = rows.find((item) => item.itemId === focusId) ?? null;
  const toPost = rows.filter((item) => checked.has(item.itemId) && item.brief);
  const failures = [...results.values()].filter((r) => !r.ok).length;

  const start = () => {
    setResults(new Map());
    post.mutate(
      {
        briefs: toPost.map((item) => ({ itemId: item.itemId, briefId: item.brief?.id ?? "" })),
        onResult: (result) => {
          setResults((prev) => new Map(prev).set(result.itemId, result));
        },
      },
      {
        onSettled: () => {
          setFinished(true);
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!post.isPending) onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-h-[calc(100vh-80px)] max-w-[680px] gap-0 overflow-y-auto border-[#27272A] bg-[#111113] p-0 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
        overlayClassName="bg-black/60"
        data-testid="post-all-dialog"
      >
        <DialogHeader className="space-y-1 px-5 pb-3 pr-10 pt-[18px] text-left">
          <DialogTitle className="font-display text-base font-semibold leading-snug">
            Post {rows.length} {rows.length === 1 ? "brief" : "briefs"} to Azure DevOps
          </DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            Each item gets one comment, posted as {posterName}. Story Points are never changed. Untick to hold one back.
          </DialogDescription>
        </DialogHeader>

        {rows.length === 0 ? (
          <p className="border-t border-[#1F1F23] px-5 py-4 text-sm text-muted-foreground">No briefs are waiting to be posted.</p>
        ) : (
          <div>
            <ul className="border-t border-[#1F1F23]" data-testid="post-all-list">
              {rows.map((item) => {
                const result = results.get(item.itemId);
                return (
                  <li
                    key={item.itemId}
                    className={cn(
                      "grid min-h-[42px] grid-cols-[15px_18px_56px_minmax(0,1fr)_auto] items-center gap-3 border-b border-[#1A1A1E] px-5 py-2 transition-colors hover:bg-[#141417]",
                      focusId === item.itemId && "bg-[#17171B] hover:bg-[#17171B]"
                    )}
                    data-testid="post-all-row"
                    data-item-id={item.itemId}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Post AB#${item.itemId}`}
                      className="h-[15px] w-[15px] cursor-pointer rounded accent-primary disabled:cursor-default"
                      checked={checked.has(item.itemId)}
                      disabled={post.isPending || finished}
                      onChange={() => {
                        const next = new Set(checked);
                        if (next.has(item.itemId)) next.delete(item.itemId);
                        else next.add(item.itemId);
                        setChecked(next);
                      }}
                    />
                    <PostStateDot result={result} />
                    <button
                      type="button"
                      className="text-left font-mono text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setFocusId(item.itemId);
                      }}
                      aria-label={`Preview AB#${item.itemId}`}
                    >
                      {item.itemId}
                    </button>
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{item.title}</span>
                      {result && (
                        <span
                          className={cn("block text-xs", result.ok ? "text-green-400" : "text-destructive")}
                          data-testid="post-all-result"
                          role={result.ok ? undefined : "alert"}
                        >
                          {resultText(result)}
                        </span>
                      )}
                    </span>
                    <span
                      className="inline-flex h-5 items-center justify-self-end whitespace-nowrap rounded-md border border-dashed border-zinc-600 px-[7px] text-[11px] font-semibold text-zinc-300"
                      title="Claude's suggestion, not the team's estimate"
                    >
                      {item.brief?.suggestedPoints} pts
                    </span>
                  </li>
                );
              })}
            </ul>
            {focus && <CommentPreview key={focus.itemId} project={project} item={focus} posterName={posterName} />}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-[#1F1F23] px-5 py-3">
          {finished ? (
            <p
              className={cn("flex-1 text-sm", failures > 0 ? "text-amber-400" : "text-green-400")}
              role="status"
              data-testid="post-all-summary"
            >
              {failures > 0
                ? `${String(results.size - failures)} posted. ${String(failures)} could not be posted and ${failures === 1 ? "stays" : "stay"} held.`
                : `${String(results.size)} posted.`}
            </p>
          ) : (
            <span className={cn("flex-1 text-xs", TOOLBOX_TEXT.faint)}>Project Managers and Admins only</span>
          )}
          {finished ? (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={post.isPending}
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="border-primary bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
                disabled={toPost.length === 0 || post.isPending}
                onClick={start}
              >
                {post.isPending
                  ? `Posting ${String(results.size + 1)} of ${String(toPost.length)}…`
                  : `Post ${String(toPost.length)} ${toPost.length === 1 ? "comment" : "comments"}`}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
